import { useState, useEffect } from "react"
import {
  Box,
  Flex,
  Text,
  Heading,
  Spinner,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Select,
  useColorModeValue,
  Button,
} from "@chakra-ui/react"
import { DownloadIcon } from "@chakra-ui/icons"
import axios from "axios"
import ExcelJS from "exceljs"

const PERIOD_OPTIONS = [
  { value: "1", label: "Last 1 Month" },
  { value: "3", label: "Last 3 Months" },
  { value: "6", label: "Last 6 Months" },
  { value: "12", label: "Last 12 Months" },
]

const ExecutiveSummary = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [periodMonths, setPeriodMonths] = useState("3")
  const [downloading, setDownloading] = useState(false)

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "gray.700")
  const kpiBgGreen = useColorModeValue("green.50", "green.900")
  const kpiBgBlue = useColorModeValue("blue.50", "blue.900")
  const kpiBgPurple = useColorModeValue("purple.50", "purple.900")
  const kpiBgTeal = useColorModeValue("teal.50", "teal.900")

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [supplier, retailer, periodMonths])

  const fetchData = async (signal) => {
    setLoading(true)
    try {
      const months = parseInt(periodMonths)
      const endDate = new Date()
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - months, 1)
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/insights/executive-summary`,
        {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
          startDate: fmt(startDate),
          endDate: fmt(endDate),
        },
        { signal }
      )
      if (res.data.status === 1) {
        setData(res.data.data)
      } else {
        setData(null)
      }
    } catch (err) {
      if (!axios.isCancel(err)) console.error("ExecutiveSummary fetch error:", err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0
    return `R ${num.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }

  const formatNum = (val) => (parseInt(val) || 0).toLocaleString()

  const handleDownload = async () => {
    if (!data) return
    setDownloading(true)
    try {
      const workbook = new ExcelJS.Workbook()
      const ws = workbook.addWorksheet("Executive Summary")

      ws.addRow([`Executive Summary — ${supplier} / ${retailer} — Last ${periodMonths} Month(s)`])
      ws.getRow(1).font = { bold: true, size: 14 }
      ws.addRow([])

      // KPIs
      ws.addRow(["HEADLINE NUMBERS"]).font = { bold: true }
      ws.addRow(["Revenue", data.kpis.total_revenue])
      ws.addRow(["Cost of Sales", data.kpis.total_cost])
      ws.addRow(["Gross Profit", data.kpis.gross_profit])
      ws.addRow(["GP %", `${(data.kpis.gp_percent || 0).toFixed(1)}%`])
      ws.addRow(["Units Sold", data.kpis.total_units])
      ws.addRow(["Active Stores", data.kpis.active_stores])
      ws.addRow(["Active Articles", data.kpis.active_articles])
      ws.addRow(["Current SOH Units", data.kpis.total_soh_qty])
      ws.addRow(["Current SOH Value", data.kpis.total_soh_value])
      ws.addRow(["Stores With Stock", data.kpis.stores_with_stock])
      ws.addRow([])

      const addTbBlock = (title, rows, isStore) => {
        ws.addRow([title]).font = { bold: true }
        ws.addRow(isStore ? ["Store", "Units", "Revenue"] : ["Article", "Brand", "Units", "Revenue"]).font = { bold: true }
        rows.forEach((r) => {
          if (isStore) {
            ws.addRow([r.site_description, parseInt(r.total_units), parseFloat(r.total_amount)])
          } else {
            ws.addRow([r.articledescription, r.brand || "-", parseInt(r.total_units), parseFloat(r.total_amount)])
          }
        })
        ws.addRow([])
      }

      addTbBlock("TOP 5 ARTICLES", data.topArticles, false)
      addTbBlock("BOTTOM 5 ARTICLES", data.bottomArticles, false)
      addTbBlock("TOP 5 STORES", data.topStores, true)
      addTbBlock("BOTTOM 5 STORES", data.bottomStores, true)

      ws.columns.forEach((col) => (col.width = 22))

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `Executive_Summary_${supplier}_${retailer}_${periodMonths}m.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Download error:", err)
    } finally {
      setDownloading(false)
    }
  }

  const TopBottomTable = ({ title, rows, isStore, isTop }) => (
    <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
      <Box p={3} borderBottom="1px" borderColor={borderColor}>
        <Heading size="sm">{title}</Heading>
      </Box>
      <Table size="sm" variant="simple">
        <Thead bg={headerBg}>
          <Tr>
            <Th w="25px">#</Th>
            <Th>{isStore ? "Store" : "Article"}</Th>
            {!isStore && <Th>Brand</Th>}
            <Th isNumeric>Revenue</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows && rows.length > 0 ? (
            rows.map((r, i) => (
              <Tr key={i} _hover={{ bg: hoverBg }}>
                <Td fontSize="xs" color="gray.500">{i + 1}</Td>
                <Td fontSize="xs" maxW="200px" isTruncated>
                  {isStore ? r.site_description : r.articledescription}
                </Td>
                {!isStore && <Td fontSize="xs">{r.brand || "-"}</Td>}
                <Td isNumeric fontSize="xs" fontWeight="bold" color={isTop ? "green.600" : "orange.500"}>
                  {formatCurrency(r.total_amount)}
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={isStore ? 3 : 4}>
                <Text color="gray.500" fontSize="xs" textAlign="center">No data</Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  )

  return (
    <Box>
      {/* Controls */}
      <Flex mb={4} gap={4} align="flex-end" wrap="wrap">
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={1}>Period</Text>
          <Select value={periodMonths} onChange={(e) => setPeriodMonths(e.target.value)} size="sm" w="160px" bg={cardBg}>
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </Box>
        {data && (
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="teal" onClick={handleDownload} isLoading={downloading} loadingText="Downloading">
            Download Report
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}><Spinner size="xl" color="teal.500" /></Flex>
      ) : !data ? (
        <Box textAlign="center" py={10}><Text color="gray.500">No data found for this period</Text></Box>
      ) : (
        <>
          {/* Headline KPIs */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
            <Stat bg={kpiBgGreen} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Revenue</StatLabel>
              <StatNumber fontSize="lg" color="green.600">{formatCurrency(data.kpis.total_revenue)}</StatNumber>
              <StatHelpText fontSize="xs" mb={0}>{formatNum(data.kpis.total_units)} units</StatHelpText>
            </Stat>
            <Stat bg={kpiBgTeal} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Gross Profit</StatLabel>
              <StatNumber fontSize="lg" color="teal.600">{formatCurrency(data.kpis.gross_profit)}</StatNumber>
              <StatHelpText fontSize="xs" mb={0}>
                GP: <b>{(data.kpis.gp_percent || 0).toFixed(1)}%</b>
              </StatHelpText>
            </Stat>
            <Stat bg={kpiBgBlue} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Current SOH Value</StatLabel>
              <StatNumber fontSize="lg" color="blue.600">{formatCurrency(data.kpis.total_soh_value)}</StatNumber>
              <StatHelpText fontSize="xs" mb={0}>{formatNum(data.kpis.total_soh_qty)} units in stores</StatHelpText>
            </Stat>
            <Stat bg={kpiBgPurple} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel fontSize="xs">Reach</StatLabel>
              <StatNumber fontSize="lg" color="purple.600">
                {formatNum(data.kpis.active_stores)} stores
              </StatNumber>
              <StatHelpText fontSize="xs" mb={0}>
                {data.kpis.active_articles} articles sold
              </StatHelpText>
            </Stat>
          </SimpleGrid>

          {/* Top/Bottom side-by-side */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={4}>
            <TopBottomTable title="Top 10 Articles (by revenue)" rows={data.topArticles} isStore={false} isTop={true} />
            <TopBottomTable title="Bottom 10 Articles (by revenue)" rows={data.bottomArticles} isStore={false} isTop={false} />
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            <TopBottomTable title="Top 10 Stores (by revenue)" rows={data.topStores} isStore={true} isTop={true} />
            <TopBottomTable title="Bottom 10 Stores (by revenue)" rows={data.bottomStores} isStore={true} isTop={false} />
          </SimpleGrid>
        </>
      )}
    </Box>
  )
}

export default ExecutiveSummary
